import { fireConfetti } from "@redotech/redo-merchant-app-common/confetti";
import { memo, useEffect } from "react";
import { createPortal } from "react-dom";
import { WalkthroughNavButtons } from "./walkthrough-nav-buttons";
import * as overlayCss from "./walkthrough-overlay.module.css";
import { WalkthroughModalStep as ModalStepType } from "./walkthrough-types";

export const WalkthroughModalStep = memo(function WalkthroughModalStep({
  step,
  stepIndex,
  totalSteps,
  next,
  back,
  dismiss,
}: {
  step: ModalStepType;
  stepIndex: number;
  totalSteps: number;
  next(): void;
  back(): void;
  dismiss(): void;
}) {
  useEffect(() => {
    if (!step.confetti) {
      return;
    }
    return fireConfetti();
  }, [step.confetti]);

  const SectionIcon = step.icon ?? step.section?.icon;

  // Render via portal to document.body so the modal escapes any ancestor
  // stacking contexts and sits above MUI Poppers / other portalled elements.
  return createPortal(
    <>
      <div className={overlayCss.modalBackdrop} />
      <div className={overlayCss.modal}>
        {step.image && (
          <img
            alt={step.title}
            className={overlayCss.modalImage}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            src={step.image}
          />
        )}
        {(SectionIcon || step.section) && (
          <div className={overlayCss.modalIconHeader}>
            <div className={overlayCss.modalHeaderText}>
              {step.section && (
                <div className={overlayCss.modalSectionLabel}>
                  Section {step.section.id}: {step.section.title}
                </div>
              )}
              <h2 className={overlayCss.modalTitle}>{step.title}</h2>
            </div>
            {SectionIcon && (
              <div className={overlayCss.modalIconCircle}>
                <SectionIcon height={20} width={20} />
              </div>
            )}
          </div>
        )}
        <div className={overlayCss.modalBody}>
          <div className={overlayCss.modalDescription}>{step.description}</div>
        </div>
        <div className={overlayCss.modalFooter}>
          <WalkthroughNavButtons
            back={back}
            dismiss={dismiss}
            next={next}
            stepIndex={stepIndex}
            totalSteps={totalSteps}
          />
        </div>
      </div>
    </>,
    document.body,
  );
});
